/**
 * The "people with access" list, with each entry's role and a way to remove it.
 *
 * Extracted from ShareModal so the default-sharing setting shows its chosen
 * audience the same way (#11877). Purely presentational.
 */

import React from "react";
import { Buildings, UsersThree, X } from "@phosphor-icons/react";
import InkSelect from "../InkSelect";
import { getInitials, type AccessItem } from "./shareParts";

interface AccessListProps {
  heading: string;
  items: AccessItem[];
  /** Labels the viewer as "(you)" and keeps their own row un-editable. */
  currentUserId: string;
  canManage: boolean;
  onChangePermission: (
    id: string,
    kind: "user" | "team",
    permission: "view" | "edit"
  ) => void;
  onRemove: (id: string, kind: "user" | "team") => void;
  /** Whether a person's role is shown and offered: false where a share
   *  grants no access, only tells them (a documents view, #11422 R6 S4). */
  showRoles?: boolean;
}

/** The read-only role of a row whose access comes through something else. */
const InheritedRole: React.FC<{ item: AccessItem }> = ({ item }) =>
  item.inheritedKind === "group" || item.inheritedKind === "folder" ? (
    // A view group's grant (#13896), or the grant of the folder an artifact
    // is filed in (#14320): "Editor, from Review Team".
    <span className="text-xs text-[var(--fp-ink-3)] text-right min-w-0 break-words">
      {`${item.role}, from ${item.inheritedFrom}`}
    </span>
  ) : (
    <span className="text-xs text-[var(--fp-ink-3)] text-right">
      {item.role}
      <span className="block">via {item.inheritedFrom}</span>
    </span>
  );

const Avatar: React.FC<{ item: AccessItem }> = ({ item }) => (
  <div
    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mr-3 flex-shrink-0 ${
      item.kind === "user"
        ? "bg-[var(--fp-band)] text-[var(--fp-ink-2)]"
        : "bg-[var(--fp-ink)] text-[var(--fp-surface)]"
    }`}
  >
    {item.kind === "user" ? (
      getInitials(item.name)
    ) : item.kind === "team" ? (
      <UsersThree size={16} weight="regular" />
    ) : (
      <Buildings size={16} weight="regular" />
    )}
  </div>
);

const AccessList: React.FC<AccessListProps> = ({
  heading,
  items,
  currentUserId,
  canManage,
  onChangePermission,
  onRemove,
  showRoles = true,
}) => (
  <div>
    <h3
      className="uppercase tracking-wider text-[var(--fp-ink-3)] mb-3"
      style={{ fontSize: "var(--fp-t-fine)", fontWeight: 600 }}
    >
      {heading}
    </h3>
    <div className="space-y-2 max-h-40 overflow-y-auto">
      {items.map((item) => {
        // A grant this entity holds itself: a person or a team.
        const grantKind = item.kind === "organization" ? null : item.kind;
        return (
          <div
            key={`${item.kind}-${item.id}-${item.inheritedFrom ?? ""}`}
            data-access-row=""
            data-state={item.unsaved ? "unsaved" : undefined}
            data-testid={
              item.unsaved ? `unsaved-grantee-${item.id}` : undefined
            }
            className="flex items-center justify-between gap-2 py-2"
          >
            <div className="flex items-center min-w-0">
              <Avatar item={item} />
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-[var(--fp-ink)] truncate">
                  {item.name}
                  {item.kind === "user" &&
                    item.id === currentUserId &&
                    " (you)"}
                </span>
                {item.kind === "user" && item.email && (
                  <span className="text-xs text-[var(--fp-ink-3)] truncate">
                    {item.email}
                  </span>
                )}
                {item.unsaved && (
                  <span className="text-xs text-[var(--fp-ink-3)]">
                    Asked for access · not saved
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              {item.role === "Owner" ? (
                <span className="text-xs text-[var(--fp-ink-3)]">Owner</span>
              ) : item.inheritedFrom || grantKind === null ? (
                <InheritedRole item={item} />
              ) : (
                <>
                  {!showRoles ? null : canManage &&
                    item.id !== currentUserId ? (
                    <InkSelect
                      value={item.permissionType}
                      options={[
                        { value: "view", label: "Viewer" },
                        { value: "edit", label: "Editor" },
                      ]}
                      onChange={(next) =>
                        onChangePermission(item.id, grantKind, next)
                      }
                      ariaLabel={`Role for ${item.name}`}
                      compact
                    />
                  ) : (
                    <span className="text-xs text-[var(--fp-ink-3)]">
                      {item.permissionType === "view" ? "Viewer" : "Editor"}
                    </span>
                  )}
                  {canManage && item.id !== currentUserId && (
                    <button
                      onClick={() => onRemove(item.id, grantKind)}
                      aria-label={`Remove ${item.name}`}
                      className="p-1 text-[var(--fp-ink-4)] hover:text-red-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default AccessList;
